import React, { type UIEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { PlanChecklistCard } from '@/components/DiaryPreschoolPlans/PlanChecklistCard';
import { PlanHtmlCard } from '@/components/DiaryPreschoolPlans/PlanHtmlCard';
import { PlanSection } from '@/components/DiaryPreschoolPlans/PlanSection';
import { PlanTextCard } from '@/components/DiaryPreschoolPlans/PlanTextCard';
import { PlanTransversalSkillsCard } from '@/components/DiaryPreschoolPlans/PlanTransversalSkillsCard';
import { ArrowLeft } from '@/components/Icons/ArrowLeft';
import { Close } from '@/components/Icons/Close';
import { Modal } from '@/components/Modal';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useScrollFadeProgress } from '@/hooks/useScrollFadeProgress';
import { useScrollShadows } from '@/hooks/useScrollShadows';
import type { PreschoolEducationPlan } from '@/types/diary';
import { formatDatePeriod } from '@/utils/date';

const TITLE_SWAP_PROGRESS = 0.5;

interface Props {
  plan: PreschoolEducationPlan;
  onClose: () => void;
}

export const PreschoolPlanModal: React.FC<Props> = ({ plan, onClose }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { progress, handleScroll } = useScrollFadeProgress();
  const {
    scrollRef,
    topProgress,
    handleScroll: handleShadowScroll,
  } = useScrollShadows();

  const achievableSkills = plan.achievableResults.flatMap(
    (result) => result.skills,
  );
  const hasContent = !!plan.content.text || !!plan.content.attachments.length;
  const hasNotes = !!plan.notes.text || !!plan.notes.attachments.length;
  const isTitleInHeader = progress > TITLE_SWAP_PROGRESS;

  const handleMobileScroll = (event: UIEvent<HTMLDivElement>) => {
    handleScroll(event);
    handleShadowScroll(event);
  };

  const content = (
    <div className="flex flex-col px-4 pt-1 pb-6 md:px-5 md:pt-0">
      <h2
        className={`text-title-36 pb-3 text-foreground transition-opacity duration-75 md:hidden ${isTitleInHeader ? 'opacity-0' : 'opacity-100'}`}
      >
        {plan.name}
      </h2>

      <p className="text-body-14 pb-2 text-subtle-foreground md:pb-0">
        {formatDatePeriod(plan.dateFrom, plan.dateTo)}
      </p>

      {!!plan.studentMessage && (
        <PlanSection title={t('diary.preschoolPlan.studentMessage')}>
          <PlanTextCard text={plan.studentMessage} />
        </PlanSection>
      )}

      {hasContent && (
        <PlanSection title={t('diary.preschoolPlan.content')}>
          <PlanHtmlCard content={plan.content} />
        </PlanSection>
      )}

      {!!plan.achievableValuesAndVirtues.length && (
        <PlanSection title={t('diary.preschoolPlan.values')}>
          <PlanChecklistCard items={plan.achievableValuesAndVirtues} />
        </PlanSection>
      )}

      {!!achievableSkills.length && (
        <PlanSection title={t('diary.preschoolPlan.skills')}>
          <PlanChecklistCard items={achievableSkills} />
        </PlanSection>
      )}

      {!!plan.transversalSkills.length && (
        <PlanSection title={t('diary.preschoolPlan.transversalSkills')}>
          <PlanTransversalSkillsCard results={plan.transversalSkills} />
        </PlanSection>
      )}

      {hasNotes && (
        <PlanSection title={t('diary.preschoolPlan.notes')}>
          <PlanHtmlCard content={plan.notes} />
        </PlanSection>
      )}
    </div>
  );

  return (
    <Modal
      isOpen
      label={plan.name}
      className="md:h-[85svh] md:w-[min(1000px,calc(100vw-40px))]"
      onClose={onClose}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <header className="flex shrink-0 items-center gap-3 px-4 pt-[calc(16px+env(safe-area-inset-top))] pb-3 md:px-5 md:pt-5 md:pb-5">
          <button
            type="button"
            aria-label={t('global.back')}
            onClick={onClose}
            className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:hidden"
          >
            <ArrowLeft />
          </button>

          <h2
            className={`text-callout-16 min-w-0 flex-1 truncate text-center text-foreground transition-opacity duration-75 md:hidden ${isTitleInHeader ? 'opacity-100' : 'opacity-0'}`}
          >
            {plan.name}
          </h2>

          <span className="size-6 shrink-0 md:hidden" />

          <h2 className="text-headline-20 hidden min-w-0 flex-1 text-foreground md:block">
            {plan.name}
          </h2>

          <button
            type="button"
            aria-label={t('global.close')}
            onClick={onClose}
            className="hidden size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:flex"
          >
            <Close />
          </button>
        </header>

        {isMobile && (
          <div className="relative flex min-h-0 flex-1 flex-col">
            <div
              style={{ opacity: topProgress }}
              className="pointer-events-none absolute inset-x-0 top-0 z-10 h-6 bg-gradient-to-b from-constant-black/6 to-transparent transition-opacity duration-300 ease-out"
            />
            <div
              ref={scrollRef}
              data-modal-scroll
              onScroll={handleMobileScroll}
              className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain"
            >
              {content}
            </div>
          </div>
        )}

        {!isMobile && (
          <AutoHideScrollArea
            hasTopShadow
            className="flex min-h-0 flex-1 flex-col"
          >
            {content}
          </AutoHideScrollArea>
        )}
      </div>
    </Modal>
  );
};
